import { FetchMethod, useCodeerApi } from "../useCodeerApi"
import { WorkspaceApiKey, Response } from "@/types"
import type { WorkspaceApiKeyRole } from "@/types"

export interface WorkspaceApiKeyResponse {
  prefix: string
  api_key?: string
  name: string
  workspace_id: string
  created_at: string
  expires_at?: string
  role?: WorkspaceApiKeyRole
}

export const workspaceApiKeyResponseToWorkspaceApiKey = (
  response: WorkspaceApiKeyResponse
): WorkspaceApiKey => {
  return {
    prefix: response.prefix,
    apiKey: response.api_key,
    name: response.name,
    workspaceId: response.workspace_id,
    createdAt: new Date(response.created_at),
    expiresAt: response.expires_at ? new Date(response.expires_at) : undefined,
    role: response.role ?? "member",
  }
}

export const workspaceApiKeysResponseToApiKeys = (
  responses: WorkspaceApiKeyResponse[]
): WorkspaceApiKey[] => {
  return responses.map(workspaceApiKeyResponseToWorkspaceApiKey)
}

export interface IssueApiKeyRequest {
  name: string
  expires_at?: string // ISO datetime string
  role?: WorkspaceApiKeyRole
}

export const makeIssueApiKeyRequest = (
  name: string,
  expires_at?: string, // ISO datetime string
  role: WorkspaceApiKeyRole = "member"
): IssueApiKeyRequest => {
  return {
    name: name,
    expires_at: expires_at,
    role: role,
  }
}

/**
 * Get all workspace API keys
 */
export const useGetWorkspaceApiKeysApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<WorkspaceApiKeyResponse[]>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/api-keys`,
    FetchMethod.Get
  )
}

/**
 * Issue a new workspace API key
 */
export const useIssueWorkspaceApiKeyApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<Response<WorkspaceApiKeyResponse>, IssueApiKeyRequest>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/api-keys`,
    FetchMethod.Post
  )
}

/**
 * Revoke (delete) a workspace API key
 */
export const useRevokeWorkspaceApiKeyApi = (
  organizationId: string,
  workspaceId: string,
  prefix: string
) => {
  return useCodeerApi<Response<void>>(
    `/organizations/${organizationId}/workspaces/${workspaceId}/api-keys/${prefix}`,
    FetchMethod.Delete
  )
}

export interface UpdateApiKeyClientsBypassWhitelistRequest {
  bypass_whitelist: boolean
}

/**
 * Update workspace API-key clients bypass whitelist setting
 */
export const useUpdateApiKeyClientsBypassWhitelistApi = (
  organizationId: string,
  workspaceId: string
) => {
  return useCodeerApi<
    Response<void>,
    UpdateApiKeyClientsBypassWhitelistRequest
  >(
    `/organizations/${organizationId}/workspaces/${workspaceId}/api-key-clients-bypass-whitelist`,
    FetchMethod.Patch
  )
}
